<template>
  <div v-if="show && open" class="wrapper state-modal-desktop flex flex-col p-6">
    <div class="flex items-center justify-between">
      <h3
          class="text-base font-semibold leading-6 text-gray-900 dark:text-white"
      >
        <h6>{{ $t('chooseState') }}</h6>
      </h3>
      <UButton
          color="gray"
          variant="ghost"
          icon="i-heroicons-x-mark-20-solid"
          class="-my-1"
          @click="emits('close')"
      />
    </div>
    <UInput
        v-model="searchInput"
        variant="none"
        :placeholder="$t('searchByState')"
        icon="i-heroicons-magnifying-glass-20-solid"
        input-class="focus:ring-gray-300 bg-gray-100 px-0 rounded-sm"
        class="button-events rounded-tl-md rounded-br-md"
        size="xl"
        :ui="{
          icon: {
            trailing: { padding: { xl: 'px-0' } },
          },
        }"
    >
    </UInput>
      <UButton
    v-if="currentState"
    class="my-2"
    color="gray"
    variant="ghost"
    icon="i-heroicons-x-circle"
    @click="clearSelection"
  >
    {{ $t('clearStateSelection') }}
  </UButton>

    <div>
      <ul class="state-list">
        <li
            v-for="item in filteredStates"
            :key="item.id"
            class="state-list-item px-2"
            @click="handleSelectState(item)"
        >
          {{ item.name }}
          <UIcon
              v-if="currentState?.id === item.id"
              size="xl"
              style="color: #1774ff; font-size: 24px"
              name="i-heroicons-check"
          />
        </li>
      </ul>
    </div>
  </div>
  <div v-if="showMobile" class="state-modal-mobile">
    <UModal
        v-model="openMobile"
        :ui="{
        width: 'full-width',
        container: 'items-end',
        padding: 'p-0',
      }"
    >
      <div class="state-modal-mobile-wrap h-[600px] overflow-hidden flex flex-col">
        <div class="state-modal-mobile-top px-[20px]">
          <h4>{{ $t('chooseState') }}:</h4>
          <div class="state-modal-mobile-close" @click="$emit('close')">
            <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
              <path
                  d="M7 7L21 21M7 21L21 7L7 21Z"
                  stroke="#1B242C"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
        <div class="state-modal-mobile-search px-[20px]">
          <UInput
              v-model="searchInput"
              variant="none"
              :placeholder="$t('searchByState')"
              icon="i-heroicons-magnifying-glass-20-solid"
              input-class="focus:ring-gray-300 bg-gray-100 px-0 rounded-sm"
              class="button-events rounded-md"
              size="sm"
              :ui="{
              icon: {
                trailing: { padding: { xl: 'px-0' } },
              },
            }"
          >
          </UInput>
        </div>
        <div class="state-modal-mobile-list overflow-y-auto grow px-[20px]">
          <UButton
              v-if="currentState"
              class="my-2"
              color="gray"
              variant="ghost"
              icon="i-heroicons-x-circle"
              @click="clearSelection"
          >
            {{ $t('clearStateSelection') }}
          </UButton>
          <div
              v-for="item in filteredStates"
              :key="item.id"
              @click="handleSelectState(item)"
              class="state-modal-mobile-item"
          >
            {{ item.name }}
            <UIcon
                v-if="currentState?.id === item.id"
                style="color: #1774ff; font-size: 24px; width: 20px; height: 20px"
                name="i-heroicons-check"
            />
          </div>
        </div>
      </div>
    </UModal>
  </div>
</template>

<script lang="ts" setup>
import {type IFilterQuery} from '@shared';
import type { IState } from '@shared/state';
import {ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import { useStateStore } from '~/store/state';

const stateStore = useStateStore();
const {states, currentState} = storeToRefs(stateStore);

const priorityStates = [
  'Illinois',
  'New York',
  'Florida',
  'Texas',
  'California',
  'Pennsylvania',
  'Washington',
  'Massachusetts',
  'New Jersey',
  'Ohio',
];

const clearSelection = async () => {
  await stateStore.setCurrentState(null); // Or however you unset it
  emits('close');
  window.location.reload();
};


const filteredStates = computed(() => {
  const search = searchInput.value.toLowerCase();

  const matchedStates = states.value.filter((state) =>
    state.name.toLowerCase().includes(search)
  );

  const prioritized = matchedStates.filter((state) =>
    priorityStates.includes(state.name)
  );

  const others = matchedStates
    .filter((state) => !priorityStates.includes(state.name))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Sort prioritized by priorityStates order
  prioritized.sort(
    (a, b) => priorityStates.indexOf(a.name) - priorityStates.indexOf(b.name)
  );

  return [...prioritized, ...others];
});

const {t} = useI18n();
const emits = defineEmits(['close']);
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
    required: false,
  },
  showMobile: {
    type: Boolean,
    default: false,
    required: false,
  },
});
const open = ref<boolean>(props.show);
const openMobile = ref<boolean>(props.showMobile);
const searchInput = ref('');

const handleSelectState = async(state: IState) => {
  emits('close');
  await stateStore.setCurrentState(state).then((response) => {
    window.location.reload();
  });
};

watch(
    () => open.value,
    (v) => {
      if (!v) emits('close');
    },
);

watch(
    () => props.show,
    (v) => {
      open.value = v;
    },
);
watch(
    () => props.showMobile,
    (v) => {
      openMobile.value = v;
    },
);

const fetchStates = async() => {
  const filterQuery: IFilterQuery = {
    filter: [],
    limit: 150,
    page: 1,
  };

  await stateStore.filter(filterQuery);
};

onMounted(async() => {
  await fetchStates();
});
</script>

<style scoped lang="scss">
.wrapper {
  position: absolute;
  width: 50%;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  box-shadow: 0 10px 20px 0 rgba(27, 36, 44, 0.05);
  z-index: 2;
  display: flex;
  gap: 16px;
  background-color: #fff;
  left: 0;
  right: 0;
  transform: translateX(30%);
  top: 80px;
}

.forms {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 24px;
}

.formsContent {
  width: 100%;
  max-width: 360px;
}

.authHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 18px;
}

.modalClose {
  width: 20px;
  cursor: pointer;
}

.active-tab {
  color: hsl(216, 100%, 55%) !important;
}

.divider {
  width: 100%;
  height: 1px;
  margin-bottom: 20px;
  background-color: var(--Text-500);
}

.state-list {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 8px;

  &-item {
    padding: 2px 2px;
    border-bottom: 1px solid #dee3e7;
    transition: all 0.2s;
    display: flex;
    justify-content: space-between;

    &:hover {
      opastate: 0.8;
      cursor: pointer;
      background-color: #eff3f3;
    }
  }
}

.state-modal-mobile {
  display: none;
}

.state-modal-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.state-modal-mobile-wrap {
  padding: 20px 0;
  border-radius: 16px;
}

.state-modal-mobile-top {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.state-modal-mobile-top h4 {
  font-weight: 700;
  color: #1b242c;
  font-size: 18px;
  line-height: 28px;
}

.state-modal-mobile-close {
  cursor: pointer;
}

.state-modal-mobile-save {
  display: flex;
  justify-content: center;
  align-items: center;
}

.state-modal-mobile-close-btn {
  border-radius: 8px;
  width: 100%;
  justify-content: center;
  align-items: center;
}

.state-modal-mobile-item {
  border-bottom: 1px solid #dee3e7;
  padding: 6px 0 6px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

@media screen and (max-width: 650px) {
  .state-modal-desktop {
    display: none;
  }
  .state-modal-mobile {
    display: block;
  }
}
</style>
